import { useState, useEffect, useMemo } from "react";
import { GuiButton } from "../../components/global-ui/gui-button";
import { GuiTextbox } from "../../components/global-ui/gui-textbox";
import { GuiSelect } from "../../components/global-ui/gui-select";
import {
  TIMEZONE_OPTIONS,
  EMAIL_EDITOR_OPTIONS,
  EMAIL_REGEX,
  MESSAGING_DEFAULTS,
} from "../../utilities/constants";
import { showToast } from "../../components/toast";
import actions from "../../actions/actions";

function mapTemplateOptions(options) {
  return (options || [])
    .map((item) => {
      const value =
        item?.value ??
        item?.id ??
        item?.templateId ??
        item?.TemplateId ??
        item?.Id ??
        null;
      const label =
        item?.label ??
        item?.name ??
        item?.text ??
        item?.templateName ??
        item?.TemplateName ??
        item?.Name ??
        (value != null ? String(value) : "");

      if (value == null || value === "") return null;
      return { value, label };
    })
    .filter(Boolean);
}

function buildMessagingForm(messagingData) {
  const source = messagingData || {};
  return {
    replyToEmail: source.replyToEmail ?? MESSAGING_DEFAULTS.replyToEmail,
    emailSendName: source.emailSendName ?? MESSAGING_DEFAULTS.emailSendName,
    callerId: source.callerId ?? MESSAGING_DEFAULTS.callerId,
    emergencyCallerId:
      source.emergencyCallerId ?? MESSAGING_DEFAULTS.emergencyCallerId,
    transferNumber: source.transferNumber ?? MESSAGING_DEFAULTS.transferNumber,
    defaultAreaCode:
      source.defaultAreaCode ?? MESSAGING_DEFAULTS.defaultAreaCode,
    timeZone: source.timeZone ?? MESSAGING_DEFAULTS.timeZone,
    defaultEmailEditor:
      source.defaultEmailEditor ?? MESSAGING_DEFAULTS.defaultEmailEditor,
    smsHeader: source.smsHeader ?? MESSAGING_DEFAULTS.smsHeader,
    preMsgTemplateId:
      source.preMsgTemplateId ?? MESSAGING_DEFAULTS.preMsgTemplateId,
    postMsgTemplateId:
      source.postMsgTemplateId ?? MESSAGING_DEFAULTS.postMsgTemplateId,
    emailResultsTo: source.emailResultsTo ?? MESSAGING_DEFAULTS.emailResultsTo,
    templateOptions: mapTemplateOptions(
      source.templateOptions ?? MESSAGING_DEFAULTS.templateOptions,
    ),
  };
}

function ViewField({ label, value, dim }) {
  return (
    <div className="acct-msg__field">
      <span className="acct-msg__label">{label}</span>
      <span className={`acct-msg__value${dim ? " acct-msg__value--dim" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function EditInput({ label, value, onChange, error, placeholder }) {
  return (
    <div className="acct-msg__field">
      <span className="acct-msg__label">{label}</span>
      <div
        className={`acct-msg__input-wrap${error ? " acct-msg__input-wrap--error" : ""}`}
      >
        <input
          className="acct-msg__input"
          value={value}
          onChange={onChange}
          placeholder={placeholder || ""}
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
        />
        {error && (
          <div className="acct-msg__input-error">
            <i className="fas fa-exclamation-circle" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function EditTextbox({ label, value, onChange, error, maxLength }) {
  return (
    <div className="acct-msg__field">
      <span className="acct-msg__label">{label}</span>
      <div
        className={`acct-msg__input-wrap${error ? " acct-msg__input-wrap--error" : ""}`}
      >
        <GuiTextbox
          className="acct-msg__textbox"
          name={label}
          value={value}
          onChange={(_name, newValue) => onChange(newValue)}
          autoComplete="off"
          maxLength={maxLength || 255}
        />
        {error && (
          <div className="acct-msg__input-error">
            <i className="fas fa-exclamation-circle" />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AccountMessaging({ messagingData, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit mode field state — seeded from API data, falls back to MESSAGING_DEFAULTS
  const [form, setForm] = useState(MESSAGING_DEFAULTS);

  useEffect(() => {
    setForm(buildMessagingForm(messagingData));
  }, [messagingData]);

  const [emailError, setEmailError] = useState("");
  const [emailResultsToError, setEmailResultsToError] = useState("");
  const [callerIdError, setCallerIdError] = useState("");

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleEditClick() {
    setEmailError("");
    setEmailResultsToError("");
    setCallerIdError("");
    setIsEditing(true);
  }

  function toTemplateIdOrZero(value) {
    if (value === "" || value == null) return 0;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return parsed;
  }

  async function saveMessaging() {
    let hasError = false;
    const replyToEmail = (form.replyToEmail || "").trim();
    const emailResultsTo = (form.emailResultsTo || "").trim();
    const callerId = (form.callerId || "").trim();

    if (!EMAIL_REGEX.test(replyToEmail)) {
      setEmailError("Invalid email");
      hasError = true;
    } else {
      setEmailError("");
    }

    if (emailResultsTo && !EMAIL_REGEX.test(emailResultsTo)) {
      setEmailResultsToError("Invalid email");
      hasError = true;
    } else {
      setEmailResultsToError("");
    }

    if (!callerId) {
      setCallerIdError("Caller ID is required");
      hasError = true;
    } else {
      setCallerIdError("");
    }

    if (hasError) return;

    setSaving(true);
    try {
      const result = await actions.account.updateAccountMessaging({
        ReplyToEmail: replyToEmail,
        EmailSendName: form.emailSendName,
        CallerId: callerId,
        EmergencyCallerId: form.emergencyCallerId,
        TransferNumber: form.transferNumber,
        DefaultAreaCode: form.defaultAreaCode,
        TimeZone: form.timeZone,
        DefaultEmailEditor: form.defaultEmailEditor,
        SMSHeader: form.smsHeader,
        PreMsgTemplateId: toTemplateIdOrZero(form.preMsgTemplateId),
        PostMsgTemplateId: toTemplateIdOrZero(form.postMsgTemplateId),
        EmailResultsTo: emailResultsTo,
      });
      if (!result) return;
      showToast("success", "Changes saved");
      setIsEditing(false);
      onSave?.();
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveSettings() {
    await saveMessaging();
  }

  function handleCancel() {
    if (!messagingData) {
      setIsEditing(false);
      return;
    }

    setEmailError("");
    setEmailResultsToError("");
    setCallerIdError("");
    setForm(buildMessagingForm(messagingData));
    setIsEditing(false);
  }

  const templateOptionLabelMap = useMemo(() => {
    const byId = {};
    (form.templateOptions || []).forEach((option) => {
      byId[String(option.value)] = option.label;
    });
    return byId;
  }, [form.templateOptions]);

  function getMessageTagLabel(tagId) {
    if (Number(tagId) === 0) return "None";

    const label = templateOptionLabelMap[String(tagId)];

    if (!label || String(label).trim() === "--select--") return "None";
    return label;
  }

  const preMessageTagLabel = getMessageTagLabel(form.preMsgTemplateId);
  const postMessageTagLabel = getMessageTagLabel(form.postMsgTemplateId);

  return (
    <div className="acct-msg">
      {/* ── Messaging Settings header ── */}
      <div className="acct-msg__section-header">
        <div className="acct-msg__title-group">
          <span className="acct-msg__section-title">Messaging Settings</span>
          <span className="acct-msg__required">*Required Information</span>
        </div>
        {!isEditing && (
          <GuiButton
            className="btn-outline acct-msg__edit-btn"
            onClick={handleEditClick}
          >
            <i className="fas fa-pen" /> Edit
          </GuiButton>
        )}
      </div>

      {/* ── Row 1: Reply-To Email / Email Send Name ── */}
      <div className="acct-msg__fields">
        {isEditing ? (
          <EditTextbox
            label="Reply-To Email*"
            value={form.replyToEmail}
            onChange={(value) => {
              setField("replyToEmail", value);
              setEmailError("");
            }}
            error={emailError}
            maxLength={100}
          />
        ) : (
          <ViewField label="Reply-To Email*" value={form.replyToEmail} />
        )}
        {isEditing ? (
          <EditTextbox
            label="Email Send Name"
            value={form.emailSendName}
            onChange={(value) => setField("emailSendName", value)}
            maxLength={100}
          />
        ) : (
          <ViewField label="Email Send Name" value={form.emailSendName} />
        )}
      </div>

      {/* ── Row 2: Caller ID / Emergency Caller ID ── */}
      <div className="acct-msg__fields">
        {isEditing ? (
          <EditTextbox
            label="Caller ID*"
            value={form.callerId}
            onChange={(value) => {
              setField("callerId", value);
              setCallerIdError("");
            }}
            error={callerIdError}
            maxLength={15}
          />
        ) : (
          <ViewField label="Caller ID*" value={form.callerId} />
        )}
        {isEditing ? (
          <EditTextbox
            label="Emergency Caller ID"
            value={form.emergencyCallerId}
            onChange={(value) => setField("emergencyCallerId", value)}
            maxLength={15}
          />
        ) : (
          <ViewField
            label="Emergency Caller ID"
            value={form.emergencyCallerId || "Not configured"}
            dim={!form.emergencyCallerId}
          />
        )}
      </div>

      {/* ── Row 3: Transfer Number / Default Area Code ── */}
      <div className="acct-msg__fields">
        {isEditing ? (
          <EditTextbox
            label="Transfer Number"
            value={form.transferNumber}
            onChange={(value) => setField("transferNumber", value)}
            maxLength={50}
          />
        ) : (
          <ViewField label="Transfer Number" value={form.transferNumber} />
        )}
        {isEditing ? (
          <EditTextbox
            label="Default Area Code"
            value={form.defaultAreaCode}
            onChange={(value) => setField("defaultAreaCode", value)}
            maxLength={6}
          />
        ) : (
          <ViewField label="Default Area Code" value={form.defaultAreaCode} />
        )}
      </div>

      {/* ── Row 4: Time Zone / Default Email Editor ── */}
      <div className="acct-msg__fields">
        {isEditing ? (
          <div className="acct-msg__field">
            <span className="acct-msg__label">Time Zone</span>
            <GuiSelect
              options={TIMEZONE_OPTIONS}
              value={form.timeZone}
              onChange={(_name, item) =>
                setField("timeZone", item?.value ?? item)
              }
            />
          </div>
        ) : (
          <ViewField label="Time Zone" value={form.timeZone} />
        )}
        {isEditing ? (
          <div className="acct-msg__field">
            <span className="acct-msg__label">Default Email Editor</span>
            <GuiSelect
              options={EMAIL_EDITOR_OPTIONS}
              value={form.defaultEmailEditor}
              onChange={(_name, item) =>
                setField("defaultEmailEditor", item?.value ?? item)
              }
            />
          </div>
        ) : (
          <ViewField
            label="Default Email Editor"
            value={form.defaultEmailEditor}
          />
        )}
      </div>

      {/* ── Row 5: SMS Header ── */}
      <div className="acct-msg__fields">
        {isEditing ? (
          <EditTextbox
            label="SMS Header"
            value={form.smsHeader}
            onChange={(value) => setField("smsHeader", value)}
            maxLength={50}
          />
        ) : (
          <ViewField label="SMS Header" value={form.smsHeader} />
        )}
      </div>

      {/* ── Save / Cancel (edit mode only) ── */}
      {/* ── Divider ── */}
      <div className="acct-msg__divider" />

      {/* ── Message Tags header ── */}
      <div className="acct-msg__section-header">
        <span className="acct-msg__section-title">Message Tags</span>
      </div>

      <div className="acct-msg__info-box">
        Pre-Message Tags play at the start of every voice alert. Post-Message
        Tags play at the end of every voice alert.
      </div>

      <div className="acct-msg__fields acct-msg__fields--vertical">
        {isEditing ? (
          <div className="acct-msg__field">
            <span className="acct-msg__label">Pre-Message Tag</span>
            <GuiSelect
              options={form.templateOptions}
              value={form.preMsgTemplateId}
              onChange={(_name, item) =>
                setField("preMsgTemplateId", item?.value ?? "")
              }
              placeholder="Select"
            />
          </div>
        ) : (
          <ViewField
            label="Pre-Message Tag"
            value={preMessageTagLabel}
            dim={preMessageTagLabel === "None"}
          />
        )}

        {isEditing ? (
          <div className="acct-msg__field">
            <span className="acct-msg__label">Post-Message Tag</span>
            <GuiSelect
              options={form.templateOptions}
              value={form.postMsgTemplateId}
              onChange={(_name, item) =>
                setField("postMsgTemplateId", item?.value ?? "")
              }
              placeholder="Select"
            />
          </div>
        ) : (
          <ViewField
            label="Post-Message Tag"
            value={postMessageTagLabel}
            dim={postMessageTagLabel === "None"}
          />
        )}

        {isEditing ? (
          <EditInput
            label="Email Results To"
            value={form.emailResultsTo}
            onChange={(e) => {
              setField("emailResultsTo", e.target.value);
              setEmailResultsToError("");
            }}
            error={emailResultsToError}
            placeholder="Add email here..."
            maxLength={100}
          />
        ) : (
          <ViewField
            label="Email Results To"
            value={form.emailResultsTo || "None"}
            dim={!form.emailResultsTo}
          />
        )}
      </div>

      {isEditing && (
        <div className="acct-msg__actions">
          <GuiButton
            className="btn-outline acct-msg__cancel-btn"
            disabled={saving}
            onClick={handleCancel}
          >
            Cancel
          </GuiButton>
          <GuiButton
            className="acct-msg__save-btn"
            disabled={saving}
            onClick={handleSaveSettings}
          >
            <i className="fas fa-save" /> {saving ? "Saving\u2026" : "Save"}
          </GuiButton>
        </div>
      )}
    </div>
  );
}
